"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { get, getToken, setToken, setUnauthorizedHandler } from "./api";
import type { LoginResponse, Me } from "./types";

type Status = "loading" | "authenticated" | "anonymous";

interface AuthState {
  status: Status;
  me: Me | null;
  /** 로그인·가입 응답을 받아 세션을 시작한다. */
  signIn: (res: LoginResponse) => void;
  signOut: () => void;
  /** 업체 등록·프로필 저장 뒤 요약 정보를 다시 읽는다. */
  refresh: () => Promise<Me | null>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<Me | null>(null);

  const signOut = useCallback(() => {
    setToken(null);
    setMe(null);
    setStatus("anonymous");
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setStatus("anonymous");
      return null;
    }
    try {
      const m = await get<Me>("/v1/auth/me");
      setMe(m);
      setStatus("authenticated");
      return m;
    } catch {
      signOut();
      return null;
    }
  }, [signOut]);

  const signIn = useCallback((res: LoginResponse) => {
    setToken(res.accessToken);
    setMe(res.user);
    setStatus("authenticated");
  }, []);

  // 첫 진입 시 저장된 토큰으로 세션을 복원한다. 상태 변경은 Promise 콜백에서만 한다.
  useEffect(() => {
    setUnauthorizedHandler(signOut);
    const token = getToken();
    const restore = token ? get<Me>("/v1/auth/me") : Promise.reject(new Error("no token"));
    restore
      .then((m) => {
        setMe(m);
        setStatus("authenticated");
      })
      .catch(() => {
        if (token) setToken(null);
        setMe(null);
        setStatus("anonymous");
      });
    return () => setUnauthorizedHandler(null);
  }, [signOut]);

  const value = useMemo(() => ({ status, me, signIn, signOut, refresh }), [status, me, signIn, signOut, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** 로그인 후 이동할 곳. 업체 등록은 가입 뒤 선택이라 업체가 없어도 대시보드로 보낸다. */
export function homePathFor(me: Me | null): string {
  if (!me) return "/login";
  return "/app";
}
